import request from "../../utils/request";
import { Result } from "../../models/general";

/*
 * Operator-only platform billing console (CO-08).
 *
 * ⚠️ Every route here is /api/PlatformBilling/* and is gated by the "Operator" policy. This module must
 * NEVER be imported from a customer-facing billing surface: Operator is a system/subscription-level
 * actor and is refused on all 23 /api/Billing/* routes by design. The reverse also holds — HR/Admin get
 * 403 on all ten routes below.
 *
 * ⚠️ NO CALL HERE SENDS AN AMOUNT THE SERVER DID NOT COMPUTE, with one deliberate exception: the manual
 * bank-transfer refund, whose amount the server CLAMPS to what was actually captured and not yet
 * refunded. An Operator can never enlarge a refund, and there is no denial path at all.
 */

/* ───────────────────────── Enums (server may send a name or a number) ───────────────────────── */

export type BillingSubscriptionState =
  | "Draft" | "Trialing" | "Active" | "NonRenewing" | "PastDue" | "Cancelled" | "Bespoke";

export type BillingAccessState = "Full" | "Grace" | "Restricted" | "Dormant";

/**
 * WHO OWNS THIS SUBSCRIPTION — the policy field. Mutable: an operator can take a subscription over
 * and can hand it back. Mirrors `BillingManagementMode` (SelfServe = 0, OperatorManaged = 1).
 */
export type BillingManagementMode = "SelfServe" | "OperatorManaged";

/**
 * HOW THIS SUBSCRIPTION CAME TO EXIST — the historical fact. Immutable once written, which is why it
 * is a separate field from the mode: a subscription the operator provisioned and later handed back is
 * still `OperatorProvisioned` in origin, and that is what settles a dispute about who started it.
 * Mirrors `BillingSubscriptionOrigin`.
 */
export type BillingSubscriptionOrigin =
  | "Unknown" | "SelfServeCheckout" | "OperatorProvisioned" | "Trial" | "Migrated";

const SUBSCRIPTION_STATES: BillingSubscriptionState[] = [
  "Draft", "Trialing", "Active", "NonRenewing", "PastDue", "Cancelled", "Bespoke",
];

const ACCESS_STATES: BillingAccessState[] = ["Full", "Grace", "Restricted", "Dormant"];

const MANAGEMENT_MODES: BillingManagementMode[] = ["SelfServe", "OperatorManaged"];

const SUBSCRIPTION_ORIGINS: BillingSubscriptionOrigin[] = [
  "Unknown", "SelfServeCheckout", "OperatorProvisioned", "Trial", "Migrated",
];

/** Mirrors `BillingInvoiceKind`. `Manual` is the operator-raised document — the only settleable one. */
export type BillingInvoiceKind =
  | "Subscription" | "Renewal" | "SeatTrueUp" | "PlanChange" | "Manual";

/** Mirrors `BillingInvoiceStatus`. */
export type BillingInvoiceStatus =
  | "Draft" | "Issued" | "Paid" | "PartiallyRefunded" | "Refunded" | "Void" | "Uncollectible";

/** Mirrors `BillingTaxTreatment`. */
export type BillingTaxTreatment =
  | "UaeStandard5" | "KsaStandard15" | "ZeroRatedExport" | "OutOfScope";

const INVOICE_KINDS: BillingInvoiceKind[] = [
  "Subscription", "Renewal", "SeatTrueUp", "PlanChange", "Manual",
];

const INVOICE_STATUSES: BillingInvoiceStatus[] = [
  "Draft", "Issued", "Paid", "PartiallyRefunded", "Refunded", "Void", "Uncollectible",
];

const TAX_TREATMENTS: BillingTaxTreatment[] = [
  "UaeStandard5", "KsaStandard15", "ZeroRatedExport", "OutOfScope",
];

export function coerceInvoiceKind(
  v: BillingInvoiceKind | number | string | null | undefined,
): BillingInvoiceKind | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return INVOICE_KINDS[v] ?? null;
  return INVOICE_KINDS.includes(v as BillingInvoiceKind) ? (v as BillingInvoiceKind) : null;
}

export function coerceInvoiceStatus(
  v: BillingInvoiceStatus | number | string | null | undefined,
): BillingInvoiceStatus | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return INVOICE_STATUSES[v] ?? null;
  return INVOICE_STATUSES.includes(v as BillingInvoiceStatus) ? (v as BillingInvoiceStatus) : null;
}

export function coerceTaxTreatment(
  v: BillingTaxTreatment | number | string | null | undefined,
): BillingTaxTreatment | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return TAX_TREATMENTS[v] ?? null;
  return TAX_TREATMENTS.includes(v as BillingTaxTreatment) ? (v as BillingTaxTreatment) : null;
}

/**
 * 🔴 CAN THIS DOCUMENT TAKE A HAND-COLLECTED PAYMENT?
 *
 * Mirrors the server's refusal exactly, and it is deliberately NARROW — `record-payment` is not a
 * generic "mark invoice paid". The server accepts ONLY a Manual (operator-raised) invoice, in Issued
 * or Uncollectible, that still owes money, on an operator-managed subscription. Offering the action
 * on anything else produces an `INVOICE_NOT_SETTLEABLE` refusal the operator cannot act on, and
 * widening it would clear a real debt and silence dunning for money that never arrived.
 */
export function canRecordPayment(
  invoice: Pick<PlatformBillingInvoiceRow, "kind" | "status" | "amountDue">,
  customerManaged: boolean,
): boolean {
  if (customerManaged) return false;
  if (coerceInvoiceKind(invoice.kind) !== "Manual") return false;
  const status = coerceInvoiceStatus(invoice.status);
  if (status !== "Issued" && status !== "Uncollectible") return false;
  return invoice.amountDue > 0;
}

/** Mirrors the existing `coerceTier` idiom: the API may return an enum NAME or its ordinal. */
export function coerceSubscriptionState(
  v: BillingSubscriptionState | number | null | undefined,
): BillingSubscriptionState | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return SUBSCRIPTION_STATES[v] ?? null;
  return SUBSCRIPTION_STATES.includes(v) ? v : null;
}

export function coerceAccessState(
  v: BillingAccessState | number | null | undefined,
): BillingAccessState | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return ACCESS_STATES[v] ?? null;
  return ACCESS_STATES.includes(v) ? v : null;
}

export function coerceManagementMode(
  v: BillingManagementMode | number | null | undefined,
): BillingManagementMode | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return MANAGEMENT_MODES[v] ?? null;
  return MANAGEMENT_MODES.includes(v) ? v : null;
}

export function coerceOrigin(
  v: BillingSubscriptionOrigin | number | null | undefined,
): BillingSubscriptionOrigin | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return SUBSCRIPTION_ORIGINS[v] ?? null;
  return SUBSCRIPTION_ORIGINS.includes(v) ? v : null;
}

/**
 * 🔴 THE FAIL-CLOSED RULE, MIRRORED EXACTLY FROM THE SERVER.
 *
 * `IBillingManagementPolicy.IsCustomerManaged` returns false when ANY of three signals says the
 * operator owns the subscription — `ManagementMode == OperatorManaged`, the legacy `OperatorManaged`
 * mirror, or `State == Bespoke`. The three can drift (the mirror survives only because ~8 Quartz/dunning
 * LINQ predicates still translate it to SQL), and the server resolves drift by DENYING.
 *
 * This client copy must resolve it the same way, or the console will label a row "the customer can
 * change this themselves" for a subscription the customer's own portal refuses to let them touch.
 * When the server has not yet sent `managementMode` at all, the two legacy signals still answer.
 */
export function isCustomerManaged(row: {
  managementMode?: BillingManagementMode | number | null;
  operatorManaged?: boolean | null;
  state?: BillingSubscriptionState | number | null;
} | null | undefined): boolean {
  if (!row) return false;
  if (coerceManagementMode(row.managementMode) === "OperatorManaged") return false;
  if (row.operatorManaged === true) return false;
  if (coerceSubscriptionState(row.state) === "Bespoke") return false;
  return true;
}

/* ───────────────────────── Views ───────────────────────── */

export interface PlatformBillingRowView {
  companyId: number;
  companyName: string;
  planCode?: string | null;
  state?: BillingSubscriptionState | number | null;
  accessState?: BillingAccessState | number | null;
  currency?: string | null;
  committedSeats?: number | null;
  /** Measured seats, from the LATEST DAILY SNAPSHOT — never a live roster scan. */
  billableSeats?: number | null;
  effectiveRatePerSeatPerMonth?: number | null;
  termEndUtc?: string | null;
  autoRenew: boolean;
  operatorManaged: boolean;
  outstandingGross: number;

  accessOverrideUntilUtc?: string | null;
  operatorOverrideReason?: string | null;
  /** True when an override is currently masking a commercial state that would otherwise restrict. */
  isOverrideDiverged: boolean;
  effectiveAccessState?: BillingAccessState | number | null;
  billableSeatsAsOfUtc?: string | null;

  /*
   * ── Management/origin. OPTIONAL on purpose ──
   * These land with the server's `AddSubscriptionManagementModeAndOrigin` work. Until every row in
   * the response carries them, `isCustomerManaged` above still answers correctly from the two legacy
   * signals, so the column degrades to "we can only tell you the legacy answer" rather than lying.
   */
  managementMode?: BillingManagementMode | number | null;
  origin?: BillingSubscriptionOrigin | number | null;
  /** Why the operator took this over. Written to the subscription audit log at the same time. */
  managementReason?: string | null;
  managementChangedAtUtc?: string | null;

  /*
   * ── PERPETUAL ENTITLEMENT. OPTIONAL on purpose ──
   * `neverExpires` is a GRANT-ONLY flag: it can only ever keep a tenant MORE entitled, never less.
   * It is NOT keyed to who manages the subscription — a self-serve tenant can be perpetual too.
   *
   * ⚠️ It is a SEPARATE CONCEPT from `accessOverrideUntilUtc`. An override is time-boxed and expires
   * on its own; this does not expire at all and is cleared only by an operator. Never render the two
   * with the same chip, and never let one imply the other.
   */
  neverExpires?: boolean | null;
  neverExpiresReason?: string | null;
  neverExpiresSetAtUtc?: string | null;
  neverExpiresSetByName?: string | null;
}

export interface PlatformOverrideAuditRow {
  id: number;
  kind: string;
  reason?: string | null;
  performedByUserId?: string | null;
  performedByName?: string | null;
  performedAt: string;
}

export interface PlatformSeatChangeRow {
  id: number;
  effectiveDateUtc: string;
  requestedDelta: number;
  netDeltaSettled?: number | null;
  status: number | string;
  source: number | string;
  amountNet?: number | null;
  settledAtUtc?: string | null;
}

export interface PlatformBillingInvoiceRow {
  id: number;
  invoiceNumber?: string | null;
  kind: number | string;
  status: number | string;
  currency: string;
  totalGross: number;
  amountPaid: number;
  issuedAtUtc?: string | null;
  /**
   * 🔴 What still has to be collected — and NOT `totalGross - amountPaid`. A Void or Uncollectible
   * document can show a positive difference while owing nothing, so the console gates
   * "Record payment" on this server-supplied figure only.
   */
  amountDue: number;
  dueAtUtc?: string | null;
}

/** CO-125. Current tax treatment, so a change can be shown as before → after. */
export interface PlatformTaxProfileView {
  taxCountry: string;
  resolvedTaxTreatment: number | string;
  resolvedTaxRatePercent: number;
  taxRegistrationNumber?: string | null;
  legalName: string;
}

export interface PlatformBillingTransactionRow {
  id: number;
  kind: number | string;
  status: number | string;
  currency: string;
  amountGross: number;
  attemptNumber: number;
  failureCode?: string | null;
  initiatedAtUtc: string;
}

export interface PlatformBillingRefundRow {
  id: number;
  reason: number | string;
  status: number | string;
  currency: string;
  amountGross: number;
  rulePath: string;
  requiresHumanApproval: boolean;
  requestedAtUtc: string;
}

/**
 * Mirrors `BillingDunningStage`. Index-aligned — the server may send a name or its ordinal.
 *
 * ⚠️ These are the LADDER's own stages and they are NOT `BillingAccessState`. The two share three
 * words and mean different things: `Grace`/`Restricted`/`Dormant` here describe where an unpaid
 * INVOICE has climbed to, while the access state is the entitlement that climb produced. `Recovered`
 * and `WrittenOff` have no access-state counterpart at all. Never coerce one with the other's helper.
 */
export type BillingDunningStage =
  | "Grace" | "Restricted" | "Dormant" | "Recovered" | "WrittenOff";

const DUNNING_STAGES: BillingDunningStage[] = [
  "Grace", "Restricted", "Dormant", "Recovered", "WrittenOff",
];

export function coerceDunningStage(
  v: BillingDunningStage | number | string | null | undefined,
): BillingDunningStage | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return DUNNING_STAGES[v] ?? null;
  return DUNNING_STAGES.includes(v as BillingDunningStage) ? (v as BillingDunningStage) : null;
}

/**
 * CO-144 — the tenant's position on the dunning / access ladder.
 *
 * 🔴 THE WHOLE OBJECT IS NULL WHEN THE TENANT IS NOT IN DUNNING, and that is the common case. Null is
 * NOT `stage: "Grace"` and NOT `attemptsMade: 0`; branch on the object before reading a field, or the
 * console will tell an operator a healthy customer has already started degrading.
 *
 * ⚠️ ONE ROW, THE WORST ONE. Dunning is per-invoice, so a tenant can be on several ladders at once.
 * The server sends the furthest-along, because that is the one governing their access; `openLadderCount`
 * says how many there are in total.
 */
export interface PlatformDunningLadderView {
  stage: BillingDunningStage | number;
  stageEnteredAtUtc: string;
  firstFailureAtUtc: string;
  attemptsMade: number;
  /** Null once the schedule is exhausted — no further automatic attempt is coming. */
  nextAttemptAtUtc?: string | null;
  /** Normalised code, never the raw gateway string. */
  lastFailureCode?: string | null;
  /** The unpaid invoice driving the ladder. */
  billingInvoiceId: number;
  /** Null while that invoice is still an unnumbered Draft. */
  invoiceNumber?: string | null;
  recoveredAtUtc?: string | null;
  openLadderCount: number;
}

export interface PlatformBillingDetailView {
  summary: PlatformBillingRowView;
  firstPaidChargeAtUtc?: string | null;
  fullRefundsUsed: number;
  creditBalance: number;
  cashOriginCreditBalance: number;
  operatorOverrideReason?: string | null;
  accessOverrideUntilUtc?: string | null;
  recentInvoices: PlatformBillingInvoiceRow[];
  recentTransactions: PlatformBillingTransactionRow[];
  recentRefunds: PlatformBillingRefundRow[];
  divergenceSummary?: string | null;
  overrideHistory: PlatformOverrideAuditRow[];
  recentSeatChanges: PlatformSeatChangeRow[];
  /** Null when the company has no BillingProfile — a real state, and the one tax-country refuses. */
  taxProfile?: PlatformTaxProfileView | null;
  neverExpiresSetByName?: string | null;
  /**
   * 🔴 Null / absent ⇒ THIS TENANT IS NOT IN DUNNING. Render an explicit "not in dunning" state — never
   * a default stage, never a zero attempt count.
   */
  dunningLadder?: PlatformDunningLadderView | null;
}

/* ─────────────── Management state + the handback preconditions ─────────────── */

/**
 * One reason a hand-back cannot happen yet. `code` is the contract; `detail` is free text the server
 * may add. The client renders a translated sentence per known code and falls back to `detail` for an
 * unknown one — an unrecognised blocker must still be READABLE, never swallowed.
 */
export interface PlatformManagementBlocker {
  code: string;
  detail?: string | null;
}

/** Blocker codes the console renders a written explanation for. */
export const HANDBACK_BLOCKER_CODES = {
  NoPaymentMethod: "NO_PAYMENT_METHOD",
  NoProviderCustomer: "NO_PROVIDER_CUSTOMER",
  OutstandingBalance: "OUTSTANDING_BALANCE",
  BillingDisabled: "BILLING_DISABLED",
  NoSubscription: "NO_SUBSCRIPTION",
  NegotiatedRate: "NEGOTIATED_RATE_NOT_IN_CATALOGUE",
} as const;

export interface PlatformManagementStateView {
  companyId: number;
  companyName?: string | null;
  managementMode?: BillingManagementMode | number | null;
  origin?: BillingSubscriptionOrigin | number | null;
  operatorManaged: boolean;
  state?: BillingSubscriptionState | number | null;
  managementReason?: string | null;
  managementChangedAtUtc?: string | null;
  managementChangedByName?: string | null;

  planCode?: string | null;
  currency?: string | null;
  termMonths?: number | null;
  termEndUtc?: string | null;
  committedSeats?: number | null;
  billableSeats?: number | null;
  effectiveRatePerSeatPerMonth?: number | null;
  autoRenew: boolean;

  /**
   * 🔴 PERPETUAL GRANT (CO-23). OPTIONAL because the server's management-state payload does not carry
   * it yet — a DTO addition is pending. Until it does, the handback dialog reads `neverExpires` off
   * the ROW, which does carry it.
   *
   * ⚠️ It is load-bearing for COPY, not decoration: on a perpetual grant nothing expires and nothing
   * is dunned, so every sentence that promises access will end is false for it. Never let a handback
   * or lapse warning branch on `autoRenew` alone — check this first. See
   * PlatformSubscriptionDialogs' management dialog and the matching server-side message in
   * PlatformBillingService.Management.cs, which must stay in step.
   */
  neverExpires?: boolean | null;

  /** Preconditions for handing the subscription back to the customer. */
  hasPaymentMethodOnFile: boolean;
  hasProviderCustomer: boolean;
  outstandingGross: number;
  canHandBack: boolean;
  handbackBlockers: PlatformManagementBlocker[];
}

/**
 * A quote for an operator-initiated change. A deliberate LOCAL MIRROR of the customer-side
 * `BillingQuoteView` rather than an import: this module must stay importable without dragging in the
 * customer billing surface, and the fields the operator console renders are a strict subset.
 *
 * ⚠️ `isCredit` is the ONLY direction signal. Every money field is an unsigned magnitude, so
 * `totalGross` alone cannot tell "charge them 2,646" from "refund them 2,646". Never infer direction
 * from a rate comparison — on the multi-year ladder the per-seat rate FALLS while the term price RISES.
 */
export interface PlatformQuoteView {
  currency: string;
  seats: number;
  termMonths: number;
  ratePerSeatPerMonth: number;
  subtotalNet: number;
  creditApplied: number;
  taxRatePercent: number;
  taxAmount: number;
  totalGross: number;
  prorationFactor: number;
  periodStartUtc?: string | null;
  periodEndUtc?: string | null;
  isCredit?: boolean;
  blockReasonCode?: string | null;
  blockMessageKey?: string | null;
}

/** The plan catalogue, read from the ANONYMOUS catalogue route so no customer-scoped call is made. */
export interface OperatorCatalogPriceView {
  currency: string;
  termMonths: number;
  pricePerSeatPerMonth: number;
}

export interface OperatorCatalogPlanView {
  code: string;
  name: string;
  description?: string | null;
  isSelfServe: boolean;
  minSeats: number;
  maxSeats?: number | null;
  displayOrder: number;
  prices: OperatorCatalogPriceView[];
}

export interface PlatformDriftRow {
  companyId?: number | null;
  companyName?: string | null;
  kind: string;
  detail: string;
  observedAtUtc?: string | null;
}

export interface PlatformDriftReportView {
  generatedAtUtc: string;
  overrideDivergences: PlatformDriftRow[];
  projectionMismatches: PlatformDriftRow[];
  stuckTransactions: PlatformDriftRow[];
  unattributedWebhooks: PlatformDriftRow[];
  totalIssues: number;
}

export interface PlatformWebhookEventRow {
  id: number;
  providerKey: string;
  providerEventId: string;
  eventType: string;
  normalizedType: number;
  companyId?: number | null;
  companyName?: string | null;
  signatureValid: boolean;
  processingStatus: number | string;
  processingError?: string | null;
  attemptCount: number;
  receivedAtUtc: string;
  processedAtUtc?: string | null;
}

/**
 * One refund parked in the Operator's release queue (CO-43).
 *
 * 🔴 BR-36 — THERE IS NO `Rejected` STATUS AND THERE NEVER WILL BE. `BillingRefundStatus` has no such
 * value by construction, so a refund has exactly two possible futures: released, or still waiting.
 * Do not add a deny/reject call to this module — there is no endpoint to call and no state to write.
 *
 * `amountNet` / `taxAmount` / `amountGross` are the calculator's output from rates frozen at purchase.
 * They are DISPLAY-ONLY here. No Operator path can change them, and the release call carries no amount.
 */
export interface PlatformPendingRefundRow {
  id: number;
  companyId: number;
  companyName: string;
  /** `PendingApproval` (never released) or `Failed` (settlement gave up — re-Release is the recovery). */
  status: number | string;
  reason: number | string;
  currency: string;
  amountNet: number;
  taxAmount: number;
  amountGross: number;
  /** The calculator's own trace of WHICH rule produced this figure — i.e. why the row was parked. */
  rulePath: string;
  consumedMonths: number;
  termMonths: number;
  requestedAtUtc: string;
  billingInvoiceId?: number | null;
  invoiceNumber?: string | null;
  /** BR-11: settlement is blocked until this exists. Null ⇒ releasing will not move money yet. */
  creditNoteId?: number | null;
  settlementAttempts: number;
  lastSettlementError?: string | null;
}

export type BillingRefundQueueStatus = "PendingApproval" | "Failed";

/** Mirrors `BillingRefundStatus`. Index-aligned: the server may send a name or its ordinal. */
const REFUND_STATUSES = [
  "Requested", "PendingApproval", "Approved", "Submitted", "Settled", "Failed", "Submitting",
] as const;

/** Mirrors `BillingRefundReason`. */
const REFUND_REASONS = [
  "MoneyBackWindow", "ProratedCancellation", "NoticeDefective",
  "OperatorGoodwill", "Chargeback", "Correction",
] as const;

export function coerceRefundStatus(v: number | string | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return REFUND_STATUSES[v] ?? null;
  return (REFUND_STATUSES as readonly string[]).includes(v) ? v : null;
}

export function coerceRefundReason(v: number | string | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return REFUND_REASONS[v] ?? null;
  return (REFUND_REASONS as readonly string[]).includes(v) ? v : null;
}

export interface PlatformBillingPage {
  items: PlatformBillingRowView[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/* ───────────────────────── Inputs ───────────────────────── */

export interface PlatformBillingListQuery {
  search?: string;
  state?: BillingSubscriptionState | "";
  accessState?: BillingAccessState | "";
  currency?: string;
  operatorManaged?: boolean;
  /** "" = every subscription, regardless of who manages it. */
  managementMode?: BillingManagementMode | "";
  origin?: BillingSubscriptionOrigin | "";
  divergedOnly?: boolean;
  page?: number;
  pageSize?: number;
}

/** ⚠️ Carries no cashOrigin and no entryType — the server forces goodwill to be non-cash. */
export interface GoodwillCreditInput {
  currency: string;
  amount: number;
  reason: string;
  notes?: string | null;
}

export interface CancelInput {
  reason: string;
  immediate: boolean;
}

export interface SetBespokeInput {
  operatorManaged: boolean;
  reason: string;
  negotiatedRatePerSeatPerMonth?: number | null;
  committedSeats?: number | null;
  termMonths?: number | null;
  termEndUtc?: string | null;
}

export interface AccessStateOverrideInput {
  /** Ordinal of BillingAccessState. The server refuses anything less generous than the current state. */
  state: number;
  untilUtc: string;
  reason: string;
}

/* ── Operator-initiated changes. EVERY ONE CARRIES A MANDATORY `reason` — it lands in the
   subscription audit log and is the artefact that settles a billing dispute six months later. ── */

export interface SetManagementModeInput {
  /** Ordinal of BillingManagementMode: 0 = SelfServe (hand back), 1 = OperatorManaged (take over). */
  mode: number;
  reason: string;

  /**
   * 🔴 CO-37 — hand back even though no payment method is on file.
   *
   * The precondition it overrides named an order that cannot exist: the customer is FORBIDDEN from
   * adding a card while the subscription is operator-managed, so "ask them to add a card first" was
   * impossible and the handback was refused forever — a deadlock only a manual DB write escaped.
   * The real order is handback FIRST, then the customer (now self-serve) adds a card. Send this only
   * after the operator has explicitly confirmed; the server audits it as its own event.
   */
  acknowledgeNoPaymentMethod?: boolean;
}

/** CO-37 — the server's refusal code for a handback with no card on file. */
export const HANDBACK_NO_PAYMENT_METHOD_CODE = "HANDBACK_NO_PAYMENT_METHOD";

/** CO-37 — settlement of a manually-invoiced document paid outside the payment provider. */
export interface PlatformRecordInvoicePaymentInput {
  invoiceId: number;
  /** Value date of the transfer. Omit for now. Never in the future. */
  paidAtUtc?: string | null;
  /** Bank transfer reference or cheque number. Billing-side only — never PII. */
  reference?: string | null;
  /** MANDATORY, like every operator override. */
  reason: string;
}

export interface PlatformPlanChangeInput {
  targetPlanCode: string;
  targetTermMonths?: number | null;
  /** Downgrades default to term end. Applying immediately yields account credit, never cash. */
  applyImmediately: boolean;
  reason: string;
}

export interface PlatformSeatChangeInput {
  /** SIGNED. Positive adds seats, negative removes them — same convention as the customer path. */
  seatDelta: number;
  /** ISO yyyy-MM-dd. Omit for today. */
  effectiveDateUtc?: string | null;
  reason: string;
}

export interface PlatformTermChangeInput {
  /** New committed term length. Mutually exclusive with `termEndUtc`. */
  termMonths?: number | null;
  /** Move the term end without re-rating. Mutually exclusive with `termMonths`. */
  termEndUtc?: string | null;
  reason: string;
}

export interface PlatformAutoRenewInput {
  autoRenew: boolean;
  reason: string;
}

/**
 * Set or clear the perpetual-entitlement flag.
 *
 * Setting it is an INDEFINITE COMMERCIAL COMMITMENT — nothing in the product will ever expire this
 * tenant again until an operator clears it here. Clearing it hands the tenant back to the normal
 * expiry ladder, which is the only direction in which this lever can reduce entitlement.
 */
export interface SetNeverExpiresInput {
  neverExpires: boolean;
  reason: string;
}

export interface ManualRefundInput {
  billingInvoiceId?: number | null;
  billingTransactionId?: number | null;
  reason: number;
  currency: string;
  amountNet: number;
  reasonNote: string;
}

/* ───────────────────────── Calls ───────────────────────── */

export async function listPlatformBilling(query: PlatformBillingListQuery) {
  // URLSearchParams rather than axios `params`, matching the existing Subscription.ts idiom — it makes
  // the omission of empty filters explicit instead of relying on undefined-serialization behaviour.
  const params = new URLSearchParams();
  params.append("page", String(query.page ?? 1));
  params.append("pageSize", String(query.pageSize ?? 25));
  if (query.search) params.append("search", query.search);
  if (query.state) params.append("state", query.state);
  if (query.accessState) params.append("accessState", query.accessState);
  if (query.currency) params.append("currency", query.currency);
  if (query.operatorManaged !== undefined) {
    params.append("operatorManaged", String(query.operatorManaged));
  }
  if (query.managementMode) params.append("managementMode", query.managementMode);
  if (query.origin) params.append("origin", query.origin);
  if (query.divergedOnly) params.append("divergedOnly", "true");

  const response = await request.get<Result<PlatformBillingPage>>(
    `PlatformBilling/companies?${params.toString()}`,
  );
  return response.data;
}

export async function getPlatformBillingDetail(companyId: number) {
  const response = await request.get<Result<PlatformBillingDetailView>>(
    `PlatformBilling/companies/${companyId}`,
  );
  return response.data;
}

export async function getDriftReport() {
  const response = await request.get<Result<PlatformDriftReportView>>("PlatformBilling/reconciliation");
  return response.data;
}

export async function listWebhookEvents(query: {
  unattributedOnly?: boolean;
  page?: number;
  pageSize?: number;
}) {
  const params = new URLSearchParams();
  params.append("page", String(query.page ?? 1));
  params.append("pageSize", String(query.pageSize ?? 50));
  if (query.unattributedOnly) params.append("unattributedOnly", "true");

  const response = await request.get<Result<{ items: PlatformWebhookEventRow[]; totalCount: number }>>(
    `PlatformBilling/webhooks?${params.toString()}`,
  );
  return response.data;
}

/**
 * The replacement for "mark them paid for three months". Strictly better: the renewal still runs on
 * schedule, the credit auto-applies oldest-first, the invoice comes out at zero and VAT stays correct.
 */
export async function issueGoodwillCredit(companyId: number, input: GoodwillCreditInput) {
  const response = await request.post<Result<unknown>>(
    `PlatformBilling/companies/${companyId}/credit`,
    input,
  );
  return response.data;
}

/** Runs the same service path — and the same refund arithmetic — the customer's own cancel runs. */
export async function cancelSubscription(companyId: number, input: CancelInput) {
  const response = await request.post<Result<PlatformBillingRowView>>(
    `PlatformBilling/companies/${companyId}/cancel`,
    input,
  );
  return response.data;
}

export async function setBespoke(companyId: number, input: SetBespokeInput) {
  const response = await request.post<Result<PlatformBillingRowView>>(
    `PlatformBilling/companies/${companyId}/bespoke`,
    input,
  );
  return response.data;
}

/** Read-and-reconcile against the provider. Never re-charges. */
export async function reconcileFromProvider(companyId: number) {
  const response = await request.post<Result<PlatformBillingRowView>>(
    `PlatformBilling/companies/${companyId}/reconcile`,
    {},
  );
  return response.data;
}

/**
 * CO-130 — the distinct endings a resend can have. `RefusedByPolicy` is NOT an error: the server
 * fails closed on an operator-managed, Bespoke or perpetual subscription because the notice would
 * describe a charge that will never be made.
 */
export type PlatformNoticeResendOutcome =
  | "Sent" | "RefusedByPolicy" | "NoAudience" | "NoCardOnFile"
  /** E13: customer-managed subscription, but the company switched billing notifications off. */
  | "NotificationsOff";

export interface PlatformNoticeResendResult {
  companyId: number;
  /** "renewal" | "card-expiry". */
  notice: string;
  sent: boolean;
  outcome: PlatformNoticeResendOutcome;
  /** A full sentence, written for an operator to read verbatim. Print it, do not re-word it. */
  detail: string;
  /**
   * The in-app copy was deduplicated and only the EMAIL went out. The server's notification key carries
   * the subscription and the term-end date and nothing that varies per attempt, while the mailer has no
   * key at all — so a same-term resend is email-only. Surfaced because "I resent it and nothing showed
   * in their bell" is otherwise indistinguishable from a failure.
   */
  emailOnly: boolean;
}

/**
 * CO-130 — resend the BR-19 advance renewal notice.
 *
 * 🔴 This does NOT re-stamp the recorded notice date and does NOT clear the BR-20 defective flag. Those
 * two columns are the customer's legal position, not bookkeeping, and a resend must leave them alone.
 * A refusal comes back 200 with `sent: false` — check `sent`, never just `errors`.
 */
export async function resendRenewalNotice(companyId: number, input: { reason: string }) {
  const response = await request.post<Result<PlatformNoticeResendResult>>(
    `PlatformBilling/companies/${companyId}/notices/renewal/resend`,
    input,
  );
  return response.data;
}

/** CO-130 — resend the card-expiring-soon notice for the tenant's default stored card. */
export async function resendCardExpiryNotice(companyId: number, input: { reason: string }) {
  const response = await request.post<Result<PlatformNoticeResendResult>>(
    `PlatformBilling/companies/${companyId}/notices/card-expiry/resend`,
    input,
  );
  return response.data;
}

/** Time-boxed entitlement GRANT. The server refuses any state less generous than the current one. */
export async function setAccessStateOverride(companyId: number, input: AccessStateOverrideInput) {
  const response = await request.post<Result<boolean>>(
    `PlatformBilling/companies/${companyId}/access-state`,
    input,
  );
  return response.data;
}

/**
 * Perpetual entitlement, on or off. Reason is mandatory on BOTH directions — "why is this customer
 * never billed out" and "why did we just put them back on the ladder" are equally load-bearing six
 * months later, and the second one is the question that gets asked angrily.
 */
export async function setNeverExpires(companyId: number, input: SetNeverExpiresInput) {
  const response = await request.post<Result<PlatformBillingRowView>>(
    `PlatformBilling/companies/${companyId}/never-expires`,
    input,
  );
  return response.data;
}

/**
 * CO-43 — the refund release queue. Cross-tenant: "which refunds, anywhere, are waiting on me?"
 *
 * This is the missing half of `issueOrReleaseRefund` below. That call takes a `refundId`, and until
 * this listing existed nothing in the product could tell an Operator what those ids were, so an
 * over-cap refund was written `PendingApproval` and then became permanently unreleasable.
 *
 * Omit `status` to get both actionable states. There is no "rejected" filter because there is no
 * rejected state (BR-36).
 */
export async function listPendingRefunds(query: {
  search?: string;
  status?: BillingRefundQueueStatus;
  page?: number;
  pageSize?: number;
}) {
  const params = new URLSearchParams();
  params.append("page", String(query.page ?? 1));
  params.append("pageSize", String(query.pageSize ?? 25));
  if (query.search) params.append("search", query.search);
  if (query.status) params.append("status", query.status);

  const response = await request.get<
    Result<{ items: PlatformPendingRefundRow[]; totalCount: number; page: number; pageSize: number }>
  >(`PlatformBilling/refunds?${params.toString()}`);
  return response.data;
}

/** Release an over-cap refund (pass refundId) or issue a manual bank-transfer one (omit it). */
export async function issueOrReleaseRefund(
  companyId: number,
  input: ManualRefundInput,
  refundId?: number,
) {
  const response = await request.post<Result<unknown>>(
    `PlatformBilling/companies/${companyId}/refund${refundId ? `?refundId=${refundId}` : ""}`,
    input,
  );
  return response.data;
}

/* ─────────────── Management mode: who owns this subscription ─────────────── */

/**
 * Read the management state AND the hand-back preconditions in one call.
 *
 * The preconditions are fetched BEFORE the dialog renders precisely so the operator is told "there is
 * no card on file, so this cannot go back to self-serve yet" while they still have the option not to
 * try — rather than typing a reason, pressing the button, and being refused.
 */
export async function getManagementState(companyId: number) {
  const response = await request.get<Result<PlatformManagementStateView>>(
    `PlatformBilling/companies/${companyId}/management`,
  );
  return response.data;
}

/**
 * Take a subscription over (mode = 1) or hand it back to the customer (mode = 0).
 *
 * ⚠️ This is NOT the same lever as `setBespoke`. Bespoke describes the COMMERCIAL arrangement — no
 * card, manual invoicing, negotiated rate. Management mode describes WHO MAY PRESS THE BUTTONS. They
 * usually move together, but a subscription can be operator-managed while still on catalogue pricing,
 * which is exactly the case the customer portal was silently letting HR/Admin self-serve.
 */
export async function setManagementMode(companyId: number, input: SetManagementModeInput) {
  const response = await request.post<Result<PlatformManagementStateView>>(
    `PlatformBilling/companies/${companyId}/management-mode`,
    input,
  );
  return response.data;
}

/* ─────────────── Operator-initiated commercial changes ─────────────── */

/** Priced by the server. The console never sends an amount, only an intent. */
export async function previewPlatformPlanChange(companyId: number, input: PlatformPlanChangeInput) {
  const response = await request.post<Result<PlatformQuoteView>>(
    `PlatformBilling/companies/${companyId}/plan/preview`,
    input,
  );
  return response.data;
}

export async function applyPlatformPlanChange(companyId: number, input: PlatformPlanChangeInput) {
  const response = await request.post<Result<PlatformBillingRowView>>(
    `PlatformBilling/companies/${companyId}/plan`,
    input,
  );
  return response.data;
}

export async function previewPlatformSeatChange(companyId: number, input: PlatformSeatChangeInput) {
  const response = await request.post<Result<PlatformQuoteView>>(
    `PlatformBilling/companies/${companyId}/seats/preview`,
    input,
  );
  return response.data;
}

export async function applyPlatformSeatChange(companyId: number, input: PlatformSeatChangeInput) {
  const response = await request.post<Result<PlatformBillingRowView>>(
    `PlatformBilling/companies/${companyId}/seats`,
    input,
  );
  return response.data;
}

/** Term length or term end. Never both — the server refuses a body carrying the two. */
export async function changePlatformTerm(companyId: number, input: PlatformTermChangeInput) {
  const response = await request.post<Result<PlatformBillingRowView>>(
    `PlatformBilling/companies/${companyId}/term`,
    input,
  );
  return response.data;
}

export async function setPlatformAutoRenew(companyId: number, input: PlatformAutoRenewInput) {
  const response = await request.post<Result<PlatformBillingRowView>>(
    `PlatformBilling/companies/${companyId}/auto-renew`,
    input,
  );
  return response.data;
}

/**
 * 🔴 CO-37 — RECORD PAYMENT OF A MANUAL (BESPOKE) INVOICE.
 *
 * Bespoke customers pay by bank transfer, and until now nothing could record that the money had
 * arrived: the invoice stayed Issued forever, so outstanding stayed positive, the divergence banner
 * never cleared and handback stayed blocked on HANDBACK_OUTSTANDING_INVOICE. The only escape was
 * issuing a credit note for money that had genuinely been paid — a lie in the ledger and in the VAT
 * return.
 *
 * ⚠️ FULL settlement only. A partial transfer has no transaction row to attribute it to, and
 * recording a part-payment as full would silence dunning for money we have not received.
 *
 * 🔴 NARROWLY SCOPED, AND THE SERVER ENFORCES IT. The endpoint accepts ONLY a Manual (operator-
 * raised) invoice, in status Issued or Uncollectible, still owing, on a Bespoke / operator-managed
 * subscription. Anything else is refused — `INVOICE_NOT_SETTLEABLE` for the wrong document,
 * `SUBSCRIPTION_CUSTOMER_MANAGED` for a customer-managed account. Without those gates this call
 * would settle any engine-raised subscription/renewal invoice with no money received and silence
 * the dunning ladder for a live debt, so do NOT surface it as a generic "mark invoice paid" action.
 * Re-calling it on an already fully-paid document is a quiet no-op, not an error.
 */
export async function recordPlatformInvoicePayment(
  companyId: number,
  invoiceId: number,
  input: PlatformRecordInvoicePaymentInput,
) {
  const response = await request.post<Result<unknown>>(
    `PlatformBilling/companies/${companyId}/invoices/${invoiceId}/record-payment`,
    input,
  );
  return response.data;
}

/* ─────────────── CO-122 — the manual invoice register ─────────────── */

export interface PlatformManualInvoiceLineInput {
  /**
   * ⚠️ An i18n KEY, never pre-rendered text. The invoice PDF re-renders per locale from this, so a
   * sentence typed here would print literally in every language.
   */
  descriptionKey: string;
  quantity: number;
  /** EX-VAT. Tax is computed by the invoice service from the tenant's treatment and is never sent. */
  unitPriceNet: number;
  periodStartUtc?: string | null;
  periodEndUtc?: string | null;
}

export interface PlatformManualInvoiceInput {
  currency: string;
  lines: PlatformManualInvoiceLineInput[];
  periodStartUtc?: string | null;
  periodEndUtc?: string | null;
  dueAtUtc?: string | null;
  notes?: string | null;
  /** False leaves an unnumbered Draft. A number is only ever burned on issue (§13.3). */
  issue: boolean;
  /** MANDATORY. */
  reason: string;
}

export interface PlatformInvoiceCorrectionInput {
  invoiceId: number;
  net: number;
  tax: number;
  reasonKey?: string;
  /** MANDATORY. */
  reason: string;
}

export interface PlatformTaxCountryInput {
  /** ISO-2, validated server-side against the Country table. `ZZ` is refused. */
  taxCountry: string;
  /** MANDATORY. */
  reason: string;
}

/**
 * Raise a manual (Bespoke) invoice. ⚠️ Amounts ARE operator-supplied here — that is what "negotiated,
 * manually invoiced" means, and this is the only route on the controller where that is true. **Tax is
 * NOT**: the invoice service computes it from the tenant's tax treatment, so a wrong VAT figure cannot
 * be typed in. Do not add a VAT field to any caller.
 */
export async function raisePlatformManualInvoice(companyId: number, input: PlatformManualInvoiceInput) {
  const response = await request.post<Result<PlatformBillingInvoiceRow>>(
    `PlatformBilling/companies/${companyId}/invoices`,
    input,
  );
  return response.data;
}

/**
 * Issue an invoice still sitting in Draft — the recovery path for a manual invoice whose issue leg
 * failed after the draft was written. Idempotent: an already-numbered invoice comes back unchanged
 * rather than burning a second number from the legal sequence.
 */
export async function issuePlatformDraftInvoice(companyId: number, invoiceId: number, reason: string) {
  const response = await request.post<Result<PlatformBillingInvoiceRow>>(
    `PlatformBilling/companies/${companyId}/invoices/${invoiceId}/issue`,
    { reason },
  );
  return response.data;
}

/**
 * ⚠️ A correction is a CREDIT NOTE, never an edit. An issued invoice has already consumed a number
 * from the legal sequence, so editing it in place is a compliance defect rather than a convenience.
 * `net` and `tax` are the amounts being credited BACK.
 */
export async function correctPlatformInvoice(companyId: number, input: PlatformInvoiceCorrectionInput) {
  const response = await request.post<Result<unknown>>(
    `PlatformBilling/companies/${companyId}/invoices/correct`,
    input,
  );
  return response.data;
}

/**
 * CO-116 / CO-125. Set the tenant's tax country.
 *
 * 🔴 FUTURE INVOICES ONLY. The customer address, whose last token IS this value, is FROZEN onto each
 * invoice at issue and drives the tax statement printed on the document. Nothing already issued is
 * rewritten, and no caller may imply otherwise.
 *
 * ⚠️ Refused for a company with no `BillingProfile` — check `detail.taxProfile` before offering it.
 */
export async function setPlatformTaxCountry(companyId: number, input: PlatformTaxCountryInput) {
  const response = await request.post<Result<BillingProfileResolvedView>>(
    `PlatformBilling/companies/${companyId}/tax-country`,
    input,
  );
  return response.data;
}

/** The subset of the server's `BillingProfileView` the console reads back after a tax change. */
export interface BillingProfileResolvedView {
  legalName: string;
  taxCountry: string;
  resolvedTaxTreatment: number | string;
  resolvedTaxRatePercent: number;
  selfServeAllowed: boolean;
  blockReasonKey?: string | null;
}

/**
 * The plan catalogue for the plan-change dialog.
 *
 * ⚠️ Deliberately the ANONYMOUS `/api/BillingCatalog` route, NOT the customer's `/api/Billing/*`
 * surface — Operator is refused on every route there by design, and reaching for one would 403.
 */
export async function listCatalogPlansForOperator(currency: string) {
  const response = await request.get<Result<OperatorCatalogPlanView[]>>(
    `BillingCatalog/plans/${encodeURIComponent(currency)}`,
  );
  return response.data;
}

/* ───────────────────────── Formatting helpers ───────────────────────── */

export function formatMoney(amount: number | null | undefined, currency?: string | null): string {
  if (amount === null || amount === undefined) return "—";
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  return currency ? `${currency} ${formatted}` : formatted;
}
